/**
 * NURBS kernel — B-spline curve math (R1).
 *
 * Rational or non-rational clamped B-splines via de Boor evaluation,
 * knot insertion, rebuild-by-collocation and chord-length interpolation.
 * Everything is a pure function over the serializable NurbsCurveData
 * payload. Numerical derivatives use central differences on the exact
 * de Boor evaluator (simple, robust, within the plan's tolerance rule).
 */

import type { Pt3 } from '../../commands/geometryMath';
import type { NurbsCurveData } from './types';
import { curveDomain } from './types';

/** Span search (The NURBS Book A2.1). */
export function findSpan(n: number, degree: number, t: number, knots: number[]): number {
    if (t >= knots[n + 1]) return n;
    if (t <= knots[degree]) return degree;
    let low = degree, high = n + 1;
    let mid = Math.floor((low + high) / 2);
    while (t < knots[mid] || t >= knots[mid + 1]) {
        if (t < knots[mid]) high = mid; else low = mid;
        mid = Math.floor((low + high) / 2);
    }
    return mid;
}

/** Non-vanishing basis functions at t (A2.2). */
export function basisFunctions(i: number, degree: number, t: number, knots: number[]): number[] {
    const N: number[] = [1];
    const left = new Array(degree + 1).fill(0);
    const right = new Array(degree + 1).fill(0);
    for (let j = 1; j <= degree; j++) {
        left[j] = t - knots[i + 1 - j];
        right[j] = knots[i + j] - t;
        let saved = 0;
        for (let r = 0; r < j; r++) {
            const denom = right[r + 1] + left[j - r];
            const temp = denom === 0 ? 0 : N[r] / denom;
            N[r] = saved + right[r + 1] * temp;
            saved = left[j - r] * temp;
        }
        N[j] = saved;
    }
    return N;
}

/** Weight of CV i (1 when non-rational). */
export function weightOf(data: NurbsCurveData, i: number): number {
    const w = data.weights?.[i];
    return typeof w === 'number' && Number.isFinite(w) && w > 0 ? w : 1;
}

/** Homogeneous de Boor evaluation (A4.1): handles rational + non-rational. */
export function evaluateCurve(data: NurbsCurveData, t: number): Pt3 {
    const { degree, knots, points } = data;
    const n = points.length - 1;
    const clamped = Math.min(Math.max(t, knots[degree]), knots[n + 1]);
    const span = findSpan(n, degree, clamped, knots);
    const N = basisFunctions(span, degree, clamped, knots);
    let x = 0, y = 0, z = 0, w = 0;
    for (let j = 0; j <= degree; j++) {
        const idx = span - degree + j;
        if (idx < 0 || idx > n) continue;
        const wi = weightOf(data, idx) * N[j];
        const p = points[idx];
        x += wi * p[0]; y += wi * p[1]; z += wi * (p[2] ?? 0); w += wi;
    }
    if (w === 0) return [x, y, z];
    return [x / w, y / w, z / w];
}

/** Unit tangent via central difference on the exact evaluator. */
export function curveTangent(data: NurbsCurveData, t: number): Pt3 {
    const [t0, t1] = curveDomain(data);
    const h = Math.max((t1 - t0) * 1e-4, 1e-7);
    const a = evaluateCurve(data, Math.max(t0, t - h));
    const b = evaluateCurve(data, Math.min(t1, t + h));
    const d: Pt3 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
    const l = Math.hypot(d[0], d[1], d[2]) || 1;
    return [d[0] / l, d[1] / l, d[2] / l];
}

/** Curvature magnitude via the circumscribed-circle formula on 3 samples. */
export function curveCurvature(data: NurbsCurveData, t: number): number {
    const [t0, t1] = curveDomain(data);
    const h = Math.max((t1 - t0) * 1e-3, 1e-6);
    const p0 = evaluateCurve(data, Math.max(t0, t - h));
    const p1 = evaluateCurve(data, t);
    const p2 = evaluateCurve(data, Math.min(t1, t + h));
    const a = Math.hypot(p1[0] - p0[0], p1[1] - p0[1], (p1[2] ?? 0) - (p0[2] ?? 0));
    const b = Math.hypot(p2[0] - p1[0], p2[1] - p1[1], (p2[2] ?? 0) - (p1[2] ?? 0));
    const c = Math.hypot(p2[0] - p0[0], p2[1] - p0[1], (p2[2] ?? 0) - (p0[2] ?? 0));
    const v1: Pt3 = [p1[0] - p0[0], p1[1] - p0[1], (p1[2] ?? 0) - (p0[2] ?? 0)];
    const v2: Pt3 = [p2[0] - p0[0], p2[1] - p0[1], (p2[2] ?? 0) - (p0[2] ?? 0)];
    const cr: Pt3 = [
        v1[1] * v2[2] - v1[2] * v2[1],
        v1[2] * v2[0] - v1[0] * v2[2],
        v1[0] * v2[1] - v1[1] * v2[0],
    ];
    const area = 0.5 * Math.hypot(cr[0], cr[1], cr[2]);
    if (a * b * c < 1e-14) return 0;
    return (4 * area) / (a * b * c);
}

/** Clamped uniform knot vector for pointCount CVs (domain [0, n-degree]). */
export function uniformClampedKnots(pointCount: number, degree: number): number[] {
    const n = pointCount - 1;
    const interior = n - degree;
    const knots: number[] = new Array(degree + 1).fill(0);
    for (let i = 1; i <= interior; i++) knots.push(i);
    for (let i = 0; i <= degree; i++) knots.push(interior + 1);
    return knots;
}

/** Control-point curve constructor (ClampCrv semantics: CVs NOT on curve). */
export function curveFromControlPoints(points: Pt3[], degree = 3, weights?: number[]): NurbsCurveData {
    const d = Math.max(1, Math.min(degree, points.length - 1));
    return {
        degree: d,
        knots: uniformClampedKnots(points.length, d),
        points: points.map((p) => [...p] as Pt3),
        weights: weights && weights.some((w) => w !== 1) ? weights : undefined,
    };
}

/**
 * Interpolating curve through exact points (3D InterpCrv semantics):
 * averaging parameters + collocation solve for the control net.
 */
export function interpolateCurve(points: Pt3[], degree = 3): NurbsCurveData | null {
    const n = points.length;
    if (n < 2) return null;
    if (n === 2) {
        return { degree: 1, knots: [0, 0, 1, 1], points: points.map((p) => [...p] as Pt3) };
    }
    const p = Math.max(1, Math.min(degree, n - 1));
    // chord-length params scaled to the target domain [0, n-p] (A9.2)
    const dist = [0];
    for (let i = 1; i < n; i++) {
        dist.push(dist[i - 1] + Math.hypot(
            points[i][0] - points[i - 1][0],
            points[i][1] - points[i - 1][1],
            (points[i][2] ?? 0) - (points[i - 1][2] ?? 0)
        ));
    }
    const total = dist[n - 1] || 1;
    const tBar = dist.map((x) => (x / total) * (n - p));
    // knot vector (9.5): interiors from averaged params, clamped ends
    const knots = new Array(n + p + 1).fill(0);
    for (let j = 1; j <= n - p; j++) {
        let s = 0;
        for (let i = j; i <= j + p - 1; i++) s += tBar[i];
        knots[j + p] = s / p;
    }
    for (let i = 0; i <= p; i++) knots[i] = 0;
    for (let i = n + 1; i <= n + p; i++) knots[i] = n - p;
    // collocation parameters (9.6)
    const params = [0];
    for (let j = 1; j <= n - p - 1; j++) {
        let s = 0;
        for (let i = j; i <= j + p - 1; i++) s += knots[i + 1];
        params.push(s / p);
    }
    params.push(n - p);
    const M: number[][] = params.map((t, i) => {
        const span = findSpan(n - 1, p, Math.min(t, knots[n] - 1e-9), knots);
        const N = basisFunctions(span, p, Math.min(t, knots[n] - 1e-9), knots);
        const row = new Array(n).fill(0);
        for (let j = 0; j <= p; j++) row[span - p + j] = N[j];
        void i;
        return row;
    });
    const solved = solveLinear(M, points.map((q) => [q[0], q[1], q[2] ?? 0] as Pt3));
    if (!solved) return { degree: p, knots, points: points.map((q) => [...q] as Pt3) };
    return { degree: p, knots, points: solved };
}

/** Gaussian elimination with partial pivoting; one rhs column per axis. */
function solveLinear(A: number[][], B: Pt3[]): Pt3[] | null {
    const n = A.length;
    if (n === 0 || B.length !== n) return null;
    const m = A.map((row, i) => [...row, B[i][0], B[i][1], B[i][2]]);
    for (let col = 0; col < n; col++) {
        let piv = col;
        for (let r = col + 1; r < n; r++) if (Math.abs(m[r][col]) > Math.abs(m[piv][col])) piv = r;
        if (Math.abs(m[piv][col]) < 1e-12) return null;
        if (piv !== col) [m[col], m[piv]] = [m[piv], m[col]];
        for (let r = 0; r < n; r++) {
            if (r === col) continue;
            const f = m[r][col] / m[col][col];
            if (f === 0) continue;
            for (let c = col; c < n + 3; c++) m[r][c] -= f * m[col][c];
        }
    }
    return m.map((row) => [row[n], row[n + 1], row[n + 2]] as Pt3);
}

/**
 * Insert one knot (A5.1, non-in-place so original CVs stay clean).
 * Rational curves are blended in homogeneous space and re-projected.
 */
export function insertKnot(data: NurbsCurveData, t: number): NurbsCurveData {
    const { degree: p, knots: U, points: P } = data;
    const n = P.length - 1;
    const rational = !!data.weights;
    const hom: [number, number, number, number][] = P.map((pt, i) => {
        const w = rational ? weightOf(data, i) : 1;
        return [pt[0] * w, pt[1] * w, (pt[2] ?? 0) * w, w];
    });
    const k = findSpan(n, p, t, U);
    const Q: [number, number, number, number][] = [];
    for (let i = 0; i <= k - p; i++) Q.push(hom[i]);
    for (let i = k - p + 1; i <= k; i++) {
        const denom = U[i + p] - U[i];
        const alpha = denom === 0 ? 0 : (t - U[i]) / denom;
        const a = hom[i], b = hom[i - 1];
        Q.push([
            b[0] + alpha * (a[0] - b[0]),
            b[1] + alpha * (a[1] - b[1]),
            b[2] + alpha * (a[2] - b[2]),
            b[3] + alpha * (a[3] - b[3]),
        ]);
    }
    for (let i = k; i <= n; i++) Q.push(hom[i]);
    const newKnots = [...U];
    newKnots.splice(k + 1, 0, t);
    if (!rational) {
        return { degree: p, knots: newKnots, points: Q.map((h) => [h[0], h[1], h[2]] as Pt3), closed: data.closed };
    }
    return {
        degree: p, knots: newKnots,
        points: Q.map((h) => [h[3] === 0 ? 0 : h[0] / h[3], h[3] === 0 ? 0 : h[1] / h[3], h[3] === 0 ? 0 : h[2] / h[3]] as Pt3),
        weights: Q.map((h) => h[3]),
        closed: data.closed,
    };
}

/** Rebuild: re-fit to `pointCount` CVs at `degree` from dense samples. */
export function rebuildCurve(data: NurbsCurveData, degree: number, pointCount: number): NurbsCurveData {
    const [t0, t1] = curveDomain(data);
    const samples: Pt3[] = [];
    for (let i = 0; i < pointCount; i++) {
        samples.push(evaluateCurve(data, t0 + ((t1 - t0) * i) / (pointCount - 1)));
    }
    return interpolateCurve(samples, degree) ?? { ...data };
}

/** Uniform-parameter polyline sample (display fallback). */
export function sampleCurve(data: NurbsCurveData, segments = 64): Pt3[] {
    const [t0, t1] = curveDomain(data);
    const out: Pt3[] = [];
    for (let i = 0; i <= segments; i++) out.push(evaluateCurve(data, t0 + ((t1 - t0) * i) / segments));
    return out;
}

/** Arc length by dense sampling of the exact evaluator. */
export function curveLength(data: NurbsCurveData, segments = 128): number {
    const pts = sampleCurve(data, segments);
    let sum = 0;
    for (let i = 1; i < pts.length; i++) {
        sum += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1], pts[i][2] - pts[i - 1][2]);
    }
    return sum;
}

/** Closest point on the curve to p: coarse scan + golden-section refine. */
export function closestPointOnCurve(data: NurbsCurveData, p: Pt3): { t: number; point: Pt3; dist: number } {
    const [t0, t1] = curveDomain(data);
    let bestT = t0, bestD = Infinity;
    const coarse = 96;
    for (let i = 0; i <= coarse; i++) {
        const t = t0 + ((t1 - t0) * i) / coarse;
        const pt = evaluateCurve(data, t);
        const d = Math.hypot(pt[0] - p[0], pt[1] - p[1], pt[2] - p[2]);
        if (d < bestD) { bestD = d; bestT = t; }
    }
    let lo = Math.max(t0, bestT - (t1 - t0) / coarse);
    let hi = Math.min(t1, bestT + (t1 - t0) / coarse);
    for (let it = 0; it < 40; it++) {
        const m1 = lo + (hi - lo) * 0.382, m2 = lo + (hi - lo) * 0.618;
        const p1 = evaluateCurve(data, m1), p2 = evaluateCurve(data, m2);
        const d1 = Math.hypot(p1[0] - p[0], p1[1] - p[1], p1[2] - p[2]);
        const d2 = Math.hypot(p2[0] - p[0], p2[1] - p[1], p2[2] - p[2]);
        if (d1 < d2) { hi = m2; if (d1 < bestD) { bestD = d1; bestT = m1; } }
        else { lo = m1; if (d2 < bestD) { bestD = d2; bestT = m2; } }
    }
    return { t: bestT, point: evaluateCurve(data, bestT), dist: bestD };
}

/**
 * Split at t into two sub-curves: insert the knot up to full multiplicity
 * (degree times), then the junction CV is shared: left = CVs 0..k-p,
 * right = CVs k-p..n, each clamped with p+1 copies of t at the seam.
 */
export function splitCurve(data: NurbsCurveData, t: number): [NurbsCurveData, NurbsCurveData] {
    const [t0, t1] = curveDomain(data);
    const mid = Math.min(Math.max(t, t0 + 1e-9), t1 - 1e-9);
    let work = data;
    for (let i = 0; i < data.degree; i++) work = insertKnot(work, mid);
    const p = work.degree;
    const n = work.points.length - 1;
    const k = findSpan(n, p, mid, work.knots);
    const leftPts = work.points.slice(0, k - p + 1);
    const rightPts = work.points.slice(k - p);
    const leftW = work.weights ? work.weights.slice(0, k - p + 1) : undefined;
    const rightW = work.weights ? work.weights.slice(k - p) : undefined;
    const leftKnots = [...work.knots.slice(0, k - p + 1), ...new Array(p + 1).fill(mid)];
    const rightBase = [...new Array(p + 1).fill(mid), ...work.knots.slice(k + 1)];
    const rStart = rightBase[p];
    const rightKnots = rightBase.map((x) => x - rStart + t0);
    return [
        { degree: p, knots: leftKnots, points: leftPts, weights: leftW },
        { degree: p, knots: rightKnots, points: rightPts, weights: rightW },
    ];
}
